import { useCallback, useEffect, useMemo, useState } from "react";
import type { AdminTagCount } from "@gooner/shared";
import { adminApi } from "../lib/api";
import { Button, TextInput } from "../control/ui/primitives";

/**
 * Catalog-wide tag maintenance: delete a tag from every clip, or merge one tag
 * into another (every clip tagged `from` gains `into`, then `from` is removed).
 * Both mutate the global clip_tags and return the refreshed counts.
 */
export function TagManager() {
  const [tags, setTags] = useState<AdminTagCount[] | null>(null);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [mergeFrom, setMergeFrom] = useState<string | null>(null);
  const [mergeInto, setMergeInto] = useState("");
  // Which tag's display name is being edited, and the in-flight draft.
  const [nameFor, setNameFor] = useState<string | null>(null);
  const [nameDraft, setNameDraft] = useState("");

  const load = useCallback(async () => {
    const { tags: list } = await adminApi.listTagCounts();
    setTags(list);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const list = tags ?? [];
    // Match the display name too — once a keyword is renamed, its shown name is what
    // an admin will think to search for, not the slug underneath it.
    return q
      ? list.filter(
          (t) =>
            t.tag.toLowerCase().includes(q) || t.resolvedName.toLowerCase().includes(q),
        )
      : list;
  }, [tags, filter]);

  async function del(tag: string) {
    if (!window.confirm(`Delete “${tag}” from every clip? This can't be undone.`)) return;
    setBusy(tag);
    try {
      setTags((await adminApi.deleteTag(tag)).tags);
    } finally {
      setBusy(null);
    }
  }

  async function merge() {
    const from = mergeFrom;
    const into = mergeInto.trim();
    if (!from || !into || from.toLowerCase() === into.toLowerCase()) return;
    setBusy(from);
    try {
      setTags((await adminApi.mergeTag(from, into)).tags);
      setMergeFrom(null);
      setMergeInto("");
    } finally {
      setBusy(null);
    }
  }

  async function toggleHidden(tag: string, hidden: boolean) {
    setBusy(tag);
    try {
      setTags((await adminApi.setTagVisibility(tag, hidden)).tags);
    } finally {
      setBusy(null);
    }
  }

  /** Save the display-name override. Submitting an empty box CLEARS it, handing the
   *  keyword back to the name derived from its slug. */
  async function saveName() {
    const tag = nameFor;
    if (!tag) return;
    setBusy(tag);
    try {
      setTags((await adminApi.setTagDisplayName(tag, nameDraft.trim())).tags);
      setNameFor(null);
      setNameDraft("");
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="space-y-4">
      <header>
        <h2 className="text-lg font-semibold text-zinc-100">Manage tags</h2>
        <p className="text-xs text-zinc-500">
          Name a tag (the label users actually see), delete it from every clip, merge one
          tag into another, or hide a tag from public surfaces (it stays on clips and here,
          just not on the browse page). Subreddit provenance tags (<code>sub:*</code>) are
          hidden automatically. Tags marked <span className="uppercase">auto</span> take
          their name from the slug — set one to override it, or clear it to go back.
        </p>
      </header>

      <datalist id="all-tags">
        {(tags ?? []).map((t) => (
          <option key={t.tag} value={t.tag} />
        ))}
      </datalist>

      <TextInput value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter tags…" />

      {tags === null ? (
        <p className="text-sm text-zinc-500">Loading…</p>
      ) : shown.length === 0 ? (
        <p className="text-sm text-zinc-500">{filter ? "No matching tags." : "No tags yet."}</p>
      ) : (
        <ul className="divide-y divide-[var(--color-edge)] overflow-hidden rounded-xl border border-[var(--color-edge)]">
          {shown.map((t) => (
            <li key={t.tag} className="flex items-center gap-3 bg-[var(--color-panel)] p-2.5">
              <span className="min-w-0 flex-1 truncate text-sm text-zinc-100">
                {/* Display name leads — it's what users see. The slug follows, since
                    it's still the identity everything else keys on. */}
                <span className={t.hidden ? "text-zinc-500" : undefined}>
                  {t.resolvedName}
                </span>{" "}
                <span className="text-zinc-500">#{t.tag} · {t.count}</span>
                {t.displayName === null ? (
                  <span
                    className="ml-2 rounded bg-zinc-700/50 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-zinc-500"
                    title="Name derived from the slug — set one to override it"
                  >
                    auto
                  </span>
                ) : null}
                {t.hidden ? (
                  <span className="ml-2 rounded bg-zinc-700/50 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-zinc-400">
                    hidden
                  </span>
                ) : null}
              </span>
              {nameFor === t.tag ? (
                <div className="flex shrink-0 items-center gap-1.5">
                  <input
                    autoFocus
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") void saveName();
                      if (e.key === "Escape") setNameFor(null);
                    }}
                    placeholder={t.resolvedName}
                    maxLength={60}
                    className="w-40 rounded border border-[var(--color-edge)] bg-[var(--color-ink)] px-2 py-1 text-xs text-zinc-100 placeholder:text-zinc-600 focus:outline-none"
                  />
                  <Button size="sm" onClick={() => void saveName()} disabled={busy === t.tag}>
                    Save
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setNameFor(null)}>
                    Cancel
                  </Button>
                </div>
              ) : mergeFrom === t.tag ? (
                <div className="flex shrink-0 items-center gap-1.5">
                  <input
                    autoFocus
                    list="all-tags"
                    value={mergeInto}
                    onChange={(e) => setMergeInto(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") void merge();
                      if (e.key === "Escape") setMergeFrom(null);
                    }}
                    placeholder="merge into…"
                    className="w-40 rounded border border-[var(--color-edge)] bg-[var(--color-ink)] px-2 py-1 text-xs text-zinc-100 placeholder:text-zinc-600 focus:outline-none"
                  />
                  <Button size="sm" onClick={() => void merge()} disabled={busy === t.tag || !mergeInto.trim()}>
                    Merge
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setMergeFrom(null)}>
                    Cancel
                  </Button>
                </div>
              ) : (
                <div className="flex shrink-0 items-center gap-1.5">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setNameFor(t.tag);
                      // Seed with the override only — leaving it blank for an
                      // auto-named keyword makes "clear it" the obvious default.
                      setNameDraft(t.displayName ?? "");
                    }}
                    disabled={busy !== null}
                  >
                    Name
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => void toggleHidden(t.tag, !t.hidden)}
                    disabled={busy === t.tag}
                  >
                    {t.hidden ? "Show" : "Hide"}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setMergeFrom(t.tag);
                      setMergeInto("");
                    }}
                    disabled={busy !== null}
                  >
                    Merge
                  </Button>
                  <Button size="sm" variant="danger" onClick={() => void del(t.tag)} disabled={busy === t.tag}>
                    Delete
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
